import { Lightning as lng, Log, Registry, Utils } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { PageDataSource } from '../../Business/cms/PageDataSource';
import { CmsConstants } from '../../Business/cms/CmsConstants';
import { VodFilterDataSource } from '../../Business/cms/VodFilterDataSource';
import { BannerDataSource } from '../../Business/cms/BannerDataSource';
import { GridDataSource } from '../../Business/cms/GridDataSource';
import { VodApi } from '../../api/VodApi';
import { CmsProperty } from '../../api/models/Shared.types';
import { _constants } from '../../components/MiniDetail';
import { CmsRibbonDummy } from '../../components/cms/CmsRibbonDummy';
import { fullUrlWithRecipe, Recipes } from '../../Business/utility/ImageRecipeUtil';
import { ChannelManager } from '../../api/ChannelManager';

export class CmsPage extends lng.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      clipping: true,
      // renderToTexture: true,
      w: w => w,
      h: h => h,
      color: Theme.Colors.Transparent,
      rect: true,
      Background: {
        visible: false,
        w: w => w,
        h: h => h,
      },
      Content: {
        color: Theme.Colors.Transparent,
        flex: { direction: 'column' },
        w: w => w,
      },
    };
  }

  _active() {
    if (this.params.menuItem) {
      this.params.backend.logService.trackPageView(this.pageId, this.params.menuItem.title);
    } else {
      // fallback if page is opened from jump to list
      this.params.backend.logService.trackPageView(this.pageId, 'CMS_PAGE');
    }
  }

  _init() {
    // console.log('[CmsPage init]', this, this.params);
    this._dummyChildren = [];
    this.pageId = '';
    if (this.params.menuItem) {
      // if CmsPage is created from menu item
      this.pageId = this.params.menuItem.pageId;
    }
    if (this.params.assetId) {
      // if CmsPage is created for fullscreen
      this.pageId = this.params.assetId;
    }

    if (this.params.fullScreen) {
      // set initial background when fullscreen
      this.tag('Background').visible = true;
      this.tag('Background').src = Utils.asset('images/background.png');
    }

    ChannelManager.channels(this.params.backend).then(channels => {
      this.channels = channels;
      this.dataSource = new PageDataSource(this.params.backend, this.pageId, channels);
      this.dataSource.load().then(data => {
        const content = this.tag('Content');
        data = this._addFilterRowDataSource(this.params.backend, this.dataSource, data);

        this.focusIndex = data.length > 0 ? (data[0] instanceof VodFilterDataSource ? 1 : 0) : -1;

        this._addRows(data);
        this._originalRibbons = [...content.children];

        if (this.params.fullScreen) {
          // load page background and set as full background
          const url = fullUrlWithRecipe(this.params.backend, this.dataSource.background, Recipes.Page.Background);
          this.tag('Background').visible = true;
          this.tag('Background').src = url ? url : Utils.asset('images/background.png');
        } else {
          // else update background of MainPage
          this.fireAncestors('$updateBackgroundImage', this.dataSource.background);
        }

        // add dummy
        this._addDummyRibbon();
        this._refocus();
      });
    });
  }

  _getFocused() {
    if (this.focusIndex !== -1) {
      return this.tag('Content').children[this.focusIndex];
    }
  }

  loadNext() {
    Registry.setTimeout(this._loadNext.bind(this));
  }

  _loadNext() {
    const content = this.tag('Content');
    // remove dummies
    this._dummyChildren.forEach(c => content.childList.remove(c));
    this._dummyChildren = [];

    this.dataSource.load().then(data => {
      this._addRows(data);

      if (data && data.length > 0) {
        // if there is still more data, add a dummy
        this._addDummyRibbon();
      }
      // todo find better way of restoring initial ribbons than having to copy the children each time.
      this._originalRibbons = [...content.children];
    });
  }

  _addDummyRibbon() {
    this._dummyChildren.push(
      this.tag('Content').childList.a({
        flexItem: { marginTop: 20 },
        type: CmsRibbonDummy,
        h: 200,
        signals: {
          startLazyLoad: this.loadNext.bind(this),
        },
      })
    );
  }

  _handleUp() {
    if (this.focusIndex > 0) {
      this.focusIndex = this.focusIndex - 1;
      return true;
    }
    return false;
  }

  _handleDown() {
    let dummySize = this._dummyChildren.length;
    if (this._filterActive) {
      dummySize = 0;
    }
    if (this.focusIndex < this.tag('Content').children.length - 1 - dummySize) {
      this.focusIndex = this.focusIndex + 1;
      return true;
    }
    return false;
  }

  _handleBack() {
    if (this.params.fullScreen) {
      this.fireAncestors('$closeModal');
      return true;
    }
    this.fireAncestors('$focusMenu');
    return true;
  }

  _centerVertical(centerY) {
    // Workaround for modals
    // Showing/hiding a modal will cause a refocus event, at which point this.finalH === 0
    // Prevent recentering in that case
    if (this.finalH === 0) return;

    // let target = this.finalH / 2;
    let target = 1080 / 2;

    if (this.tag('Content').children[this.focusIndex].data.cmsProperties) {
      if (!this.tag('Content').children[this.focusIndex].data.cmsProperties.includes('HIDE_MINI_DETAIL')) {
        target = (1080 - _constants.miniDetailHeight) / 2;
      }
    }

    const result = Math.min(0, target - centerY);
    this.tag('Content').setSmooth('y', result);
  }

  /**
   * @param {CmsFilterData} filter
   * @private
   */
  _updateFilter(filter) {
    Log.info('[CmsPage] Filter changed', filter);
    if (!filter.category && !filter.year && !filter.rating) {
      this._restorePageRibbons();
      return;
    }
    this._setFilteredRibbons({
      url: VodApi.vodFilterUrl(this.params.backend, filter),
      title: '',
      properties: [CmsProperty.HideInfo],
      message: '',
    });
  }

  /**
   * @param {ICustomCmsGrid} grid
   * @private
   */
  _setFilteredRibbons(grid) {
    const dataSource = new GridDataSource(this.params.backend, grid, [], []);
    const content = this.tag('Content');
    content.childList.clear();
    if (this._originalRibbons.length > 0) {
      if (
        this._originalRibbons[0].data instanceof BannerDataSource ||
        this._originalRibbons[0].data instanceof VodFilterDataSource
      ) {
        content.childList.a(this._originalRibbons[0]);
      }
    }
    if (this._originalRibbons.length > 1) {
      if (this._originalRibbons[1].data instanceof VodFilterDataSource) {
        content.childList.a(this._originalRibbons[1]);
      }
    }
    dataSource.load().then(() => {
      this._addRow(dataSource);
    });
    this._filterActive = true;
  }

  _restorePageRibbons() {
    if (this._filterActive) {
      const content = this.tag('Content');
      content.childList.clear();
      content.children = [...this._originalRibbons];
    }
    this._filterActive = false;
  }

  /**
   * Adds a filter row if required
   * @param {Backend} backend
   * @param {PageDataSource} pageData
   * @param {DataSource[]} data
   * @returns {DataSource[]}
   * @private
   */
  _addFilterRowDataSource(backend, pageData, data) {
    const filterDataSource = new VodFilterDataSource(backend, pageData.properties, pageData.tags);
    if (filterDataSource.isEmpty()) {
      return data;
    }
    if (data && data.length > 0) {
      const first = data[0];
      if (first instanceof BannerDataSource) {
        data.splice(1, 0, filterDataSource);
      } else {
        data.unshift(filterDataSource);
      }
    }
    return data;
  }

  _addRow(dataSource) {
    this._addRows([dataSource]);
  }

  _addRows(dataSources) {
    const content = this.tag('Content');
    let contentOffset = CmsConstants.Ribbon.pageContentOffset;
    if (this.params.fullScreen) contentOffset = CmsConstants.Ribbon.defaultContentOffset;
    dataSources.forEach(ds => {
      content.childList.a({
        flexItem: { marginTop: 20 },
        signals: { centerVertical: '_centerVertical', updateFilter: '_updateFilter' },
        w: this.finalW,
        contentOffset,
        ...ds.componentDescription(),
      });
    });
  }
}
